import { Routes, Route, useLocation } from 'react-router-dom'
import { useStytchMember, useStytchB2BClient, useStytchOrganization } from '@stytch/react/b2b'
import { useLayoutEffect, useEffect, lazy, Suspense } from 'react'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import Login from './pages/Login'
import ProtectedRoute from './components/ProtectedRoute'
import Navigation from './components/Navigation'
import UserMenu from './components/UserMenu'
import { Toaster } from './components/ui/sonner'
import { useApiConfig } from './hooks/useApiConfig'
import { identifyUser, resetUser, trackPageView } from './lib/analytics'

// Lazy load pages that require authentication
const Home = lazy(() => import('./pages/Home'))
const Orders = lazy(() => import('./pages/Orders'))
const NotFound = lazy(() => import('./pages/NotFound'))

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30 * 1000, // 30 seconds
            refetchOnWindowFocus: true, // Refetch when user returns to the tab
            refetchOnMount: true, // Refetch when component mounts
            retry: 1,
        },
    },
})

const App = () => {
    const { member, isInitialized } = useStytchMember()
    const { organization } = useStytchOrganization()
    const stytch = useStytchB2BClient()
    const location = useLocation()

    // Configure API client
    useApiConfig()

    // Set user context for Sentry after successful authentication
    useLayoutEffect(() => {
        if (member) {
            Sentry.setUser({
                id: member.member_id,
                email: member.email_address,
                username: member.name,
            })
        } else {
            Sentry.setUser(null)
        }
    }, [member])

    // Identify the current user in Google Analytics
    useEffect(() => {
        if (member && organization) {
            identifyUser({
                memberId: member.member_id,
                organizationId: organization.organization_id,
            })
        } else if (isInitialized && !member) {
            resetUser()
        }
    }, [member, organization, isInitialized])

    // Track SPA page views in Google Analytics
    useEffect(() => {
        trackPageView(location.pathname + location.search)
    }, [location.pathname, location.search])

    // Handle token refresh and unauthorized errors
    useEffect(() => {
        const handleRefreshRequested = async () => {
            try {
                // Try to refresh the session
                await stytch.session.authenticate()
                console.log('Session refreshed successfully')
                // Notify success
                window.dispatchEvent(
                    new CustomEvent('auth:refresh-result', {
                        detail: { success: true },
                    }),
                )
            } catch (error) {
                console.error('Failed to refresh session:', error)
                // Notify failure
                window.dispatchEvent(
                    new CustomEvent('auth:refresh-result', {
                        detail: { success: false, error },
                    }),
                )
            }
        }

        const handleUnauthorized = async () => {
            console.error('Unauthorized, logging out')
            try {
                await stytch.session.revoke()
            } catch (revokeError) {
                console.error('Error revoking session:', revokeError)
            }
        }

        window.addEventListener('auth:refresh-requested', handleRefreshRequested)
        window.addEventListener('auth:unauthorized', handleUnauthorized)
        return () => {
            window.removeEventListener('auth:refresh-requested', handleRefreshRequested)
            window.removeEventListener('auth:unauthorized', handleUnauthorized)
        }
    }, [stytch])

    return (
        <QueryClientProvider client={queryClient}>
            <div className="min-h-screen flex flex-col bg-muted/30">
                {isInitialized && member && (
                    <nav className="bg-primary">
                        <div className="w-full px-4">
                            <div className="flex items-center justify-between" style={{ height: '50px' }}>
                                <Navigation />
                                <UserMenu />
                            </div>
                        </div>
                    </nav>
                )}
                <main className="flex-1">
                    <Suspense fallback={<div className="flex-1 flex items-center justify-center">Loading...</div>}>
                        <Routes>
                            <Route
                                path="/"
                                element={
                                    <ProtectedRoute>
                                        <Home />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/orders"
                                element={
                                    <ProtectedRoute>
                                        <Orders />
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="/login" element={<Login />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Suspense>
                </main>
                <Toaster />
            </div>
        </QueryClientProvider>
    )
}

export default App
